import { apiErrorName } from '@/lib/recepthor-api';
import { Button, HelperText, Input, InputRow, Modal, Select } from '@/ui';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import StatusPill from '../StatusPill/StatusPill';

import './OrgModales.css';

const REMOVE_CONFIRM = 'borrar';

// The hierarchy refusals the API answers with: only the owner acts on an
// admin, nobody acts on their own membership, and the owner's cannot be
// changed.
function hierarchyFailureMessage(err) {
  const name = apiErrorName(err?.body);
  if (name === 'OwnerOnlyAction') {
    return 'Solo el propietario puede actuar sobre un administrador.';
  }
  if (name === 'CannotModifySelf') {
    return 'No puedes actuar sobre tu propia cuenta.';
  }
  if (name === 'CannotModifyOwner') {
    return 'El propietario no se puede modificar.';
  }
  return 'No se pudo completar la acción. Intenta nuevamente.';
}

export function ManageMiembroModal({
  open,
  onClose,
  onSave,
  onRemove,
  miembro,
  templates = [],
}) {
  const [templateId, setTemplateId] = useState(miembro?.templateId ?? '');
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Closing mid-request would hide the reason a rejected change gives, since
  // the next open clears the error.
  const close = () => {
    if (!busy) onClose?.();
  };

  useEffect(() => {
    if (open) {
      setTemplateId(miembro?.templateId ?? '');
      setConfirmingRemove(false);
      setConfirmText('');
      setError('');
    }
  }, [open, miembro]);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await onSave?.({ ...miembro, templateId });
      onClose?.();
    } catch (err) {
      setError(hierarchyFailureMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleRequestRemove() {
    setConfirmingRemove(true);
  }

  async function handleConfirmRemove() {
    if (confirmText.trim().toLowerCase() !== REMOVE_CONFIRM) return;
    setError('');
    setBusy(true);
    try {
      await onRemove?.(miembro);
      onClose?.();
    } catch (err) {
      setError(hierarchyFailureMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (confirmingRemove) {
    const canConfirm = confirmText.trim().toLowerCase() === REMOVE_CONFIRM;
    return (
      <Modal open={open} onClose={close} size="small">
        <Modal.Header title="Remover del equipo" onClose={close} />
        <Modal.Body>
          <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Estás por remover a <b>{miembro?.nombre}</b> del equipo. Esta acción
            es inmediata y revoca todos sus accesos.
          </p>
          <InputRow label={`Escribe "${REMOVE_CONFIRM}" para confirmar`}>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              autoComplete="off"
            />
          </InputRow>
          {error && <HelperText kind="err">{error}</HelperText>}
        </Modal.Body>
        <Modal.Foot>
          <Button
            variant="ghost"
            type="button"
            onClick={() => setConfirmingRemove(false)}
            disabled={busy}
          >
            Volver
          </Button>
          <Button
            variant="danger-solid"
            type="button"
            disabled={!canConfirm || busy}
            onClick={handleConfirmRemove}
          >
            {busy ? 'Removiendo…' : 'Remover del equipo'}
          </Button>
        </Modal.Foot>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={close}>
      <Modal.Header title={miembro?.nombre ?? 'Miembro'} onClose={close} />
      <form onSubmit={handleSave}>
        <Modal.Body>
          <InputRow label="Correo">
            <span className="org-modal-value mono">{miembro?.correo}</span>
          </InputRow>
          <InputRow label="Ingreso">
            <span className="org-modal-value mono">{miembro?.ingreso}</span>
          </InputRow>
          <InputRow label="Estado">
            <StatusPill kind="member" value={miembro?.estado ?? 'active'} />
          </InputRow>
          <InputRow label="Rol">
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </InputRow>
          {error ? (
            <HelperText kind="err">{error}</HelperText>
          ) : (
            <HelperText>
              Cambiar el rol modifica los permisos del miembro inmediatamente.
            </HelperText>
          )}
        </Modal.Body>
        <Modal.Foot>
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            variant="danger-solid"
            type="button"
            className="org-foot-left"
            onClick={handleRequestRemove}
            disabled={busy}
          >
            Remover del equipo
          </Button>
          <Button variant="seal" type="submit" disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </Modal.Foot>
      </form>
    </Modal>
  );
}

ManageMiembroModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
  miembro: PropTypes.shape({
    nombre: PropTypes.string,
    correo: PropTypes.string,
    ingreso: PropTypes.string,
    estado: PropTypes.string,
    rol: PropTypes.string,
    templateId: PropTypes.string,
  }),
  templates: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ),
};

export default ManageMiembroModal;
